import { Link } from "react-router";
import { ArrowLeft, Calendar, CheckCircle, Move, Clock } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Main } from "@/components/shared/Main";
import { Header } from "@/components/shared/Header";

export function PlanMeasurementRequestsDocPage() {
  return (
    <>
      <Header />
      <Main>
        <div className="mb-8">
          <Button asChild variant="ghost" className="mb-4">
            <Link to="/docs" className="flex items-center">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>

          <div className="flex items-center mb-4">
            <div className="w-12 h-12 rounded-xl bg-linear-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-4">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Plan Measurement Requests</h1>
              <p className="text-xl text-gray-600">Step-by-step guide</p>
            </div>
          </div>
        </div>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 mb-4">
              Planning is the process of assigning individual wafers or a selection of wafers to be
              scheduled for testing on a{" "}
              <a
                href="/docs/glossary#:~:text=Measurement%20Setup"
                className="text-blue-600 hover:underline"
              >
                measurement setup
              </a>
              . This workflow helps organize and prioritize
              <a
                href="/docs/glossary#:~:text=Test%20Request"
                className="text-blue-600 hover:underline"
              >
                measurement requests
              </a>{" "}
              to optimize testing efficiency and resource allocation.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Prerequisites:</h4>
              <ul className="text-blue-800 text-sm">
                <li>
                  • Your role is{" "}
                  <a
                    href="/docs/glossary#:~:text=Project%20Manager"
                    className="text-blue-600 hover:underline"
                  >
                    project manager
                  </a>{" "}
                  or Admin
                </li>
                <li>
                  • At least one{" "}
                  <a
                    href="/docs/glossary#:~:text=Test%20Request"
                    className="text-blue-600 hover:underline"
                  >
                    measurement request
                  </a>{" "}
                  created
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Interface Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center">Masks Filter</h3>
              <p className="text-gray-700 text-sm">
                Use the masks filter at the top of the page to filter all content and focus on
                specific masks.
              </p>
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-gray-900 mb-3">Planning Sections</h3>

              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-semibold text-blue-900 mb-2">Scheduled Section</h4>
                  <p className="text-blue-800 text-sm mb-2">
                    Shows all measurement requests and associated wafers already scheduled on
                    measurement setups.
                  </p>
                  <ul className="text-blue-800 text-xs ml-4 space-y-1">
                    <li>• Items displayed in priority order (top = first priority)</li>
                    <li>• Drag & drop to reorder measurement requests or wafers within requests</li>
                    <li>• Use tabs to filter by measurement setup</li>
                    <li>• Numbers show: wafers planned + total estimated time remaining</li>
                  </ul>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-yellow-900 mb-2">Processing Section</h4>
                  <p className="text-yellow-800 text-sm">
                    Access via the "Processing" tab. Shows all ongoing measurement requests and
                    their results.
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="font-semibold text-green-900 mb-2">History Section</h4>
                  <p className="text-green-800 text-sm">
                    Access via the "History" tab. Shows all completed measurement requests and their
                    results.
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-semibold text-gray-900 mb-2">Backlog</h4>
                  <p className="text-gray-800 text-sm">
                    Contains all measurement requests that are created but not yet planned for any
                    measurement setup.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Step-by-Step Workflow</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                1
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Navigate to Request Planning</h3>
                <p className="text-gray-700 mb-2">
                  Access the request planning interface to manage measurement scheduling.
                </p>
                <Button asChild size="sm" variant="outline">
                  <Link to="/request-planning">Go to Request Planning</Link>
                </Button>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                2
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Schedule Requests from Backlog</h3>
                <p className="text-gray-700 mb-2">
                  Move measurement requests from backlog to scheduled:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>• Select the measurement request or individual wafers from the backlog</li>
                  <li>
                    • Choose a measurement system from the "Select a measurement system" dropdown
                  </li>
                  <li>• Click "Confirm Scheduling" to assign to the selected setup</li>
                </ul>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                3
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Manage Scheduling Priority</h3>
                <p className="text-gray-700 mb-2">Organize the measurement queue by priority:</p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>
                    • <strong>Reorder requests:</strong> Drag & drop measurement requests to change
                    priority
                  </li>
                  <li>
                    • <strong>Reorder wafers:</strong> Drag & drop wafers within a measurement
                    request
                  </li>
                  <li>
                    • <strong>Filter by setup:</strong> Use tabs to focus on specific measurement
                    setups
                  </li>
                </ul>
                <div className="flex items-center space-x-2 mt-2 text-sm text-gray-600">
                  <Move className="w-4 h-4" />
                  <span>Use drag & drop for easy reordering</span>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                4
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Mark Measurements as Complete</h3>
                <p className="text-gray-700 mb-2">
                  When measurements are finished, update the system:
                </p>
                <ul className="text-gray-700 text-sm ml-4 space-y-1">
                  <li>• Select the measured wafers or entire measurement request</li>
                  <li>• Click "Mark as Measured" button</li>
                  <li>• Fill in the measurement details in the dialog:</li>
                  <li className="ml-4">- Start time of the measurement</li>
                  <li className="ml-4">- Duration of the measurement</li>
                </ul>
                <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200 mt-3">
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <p className="text-yellow-800 text-sm font-medium">
                      Important: Fill in timing details carefully - this data helps refine future
                      estimations.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 text-blue-600 rounded-full font-semibold text-sm shrink-0">
                5
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Move Wafers Back to Backlog</h3>
                <p className="text-gray-700">
                  If needed, you can move wafers back from scheduled to the backlog by clicking the
                  X button next to them in the scheduled section.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              Key Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">Good to Know:</h4>
              <p className="text-blue-800 text-sm">
                A measurement request can appear in backlog, scheduled, or measured sections
                simultaneously when their linked wafers are in different stages of the planning
                workflow. This allows for flexible, granular control over individual wafer
                scheduling.
              </p>
            </div>

            <div className="mt-4">
              <h4 className="font-semibold text-gray-900 mb-2">Setup Information Display:</h4>
              <p className="text-gray-700 text-sm mb-2">Each measurement setup tab shows:</p>
              <ul className="text-gray-700 text-sm ml-4 space-y-1">
                <li>• Number of wafers currently planned</li>
                <li>• Total estimated measurement time remaining</li>
                <li>• Priority order of measurements</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-between">
          <Button asChild variant="outline">
            <Link to="/docs">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Documentation
            </Link>
          </Button>
          <Button asChild>
            <Link to="/request-planning">Go to Request Planning</Link>
          </Button>
        </div>
      </Main>
    </>
  );
}
